import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Loader2, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";

const TIERS = [
  {
    code: "free",
    name: "Free",
    monthly: 0,
    annual: 0,
    tagline: "Taste the magic",
    lines: [
      "10 generations total · never resets",
      "Live 72-hour feed",
      "Up to 2 niches",
      "Save up to 10 packets",
    ],
    pkg_monthly: null,
    pkg_annual: null,
    accent: false,
  },
  {
    code: "solo",
    name: "Solo",
    monthly: 9.99,
    annual: 95.90,
    tagline: "Side-project podcasters",
    lines: [
      "30 generations / month",
      "Daily Brief email at 06:00 UTC",
      "Up to 3 niches",
      "50 saved packets",
    ],
    pkg_monthly: "solo_monthly",
    pkg_annual: "solo_annual",
    accent: false,
  },
  {
    code: "pro",
    name: "Pro",
    monthly: 14.99,
    annual: 143.90,
    tagline: "Working creators",
    lines: [
      "150 generations / month",
      "Daily Brief + Weekly Calendar",
      "Up to 8 niches",
      "Custom RSS feeds (up to 15)",
      "Unlimited saved packets",
    ],
    pkg_monthly: "pro_monthly",
    pkg_annual: "pro_annual",
    accent: true,
  },
  {
    code: "studio",
    name: "Studio",
    monthly: 24.99,
    annual: 239.90,
    tagline: "Multi-show studios",
    lines: [
      "Unlimited generations",
      "Daily Brief + custom send time",
      "Unlimited custom RSS feeds",
      "Unlimited niches",
      "Up to 5 creator profiles",
      "Branded email digest",
      "Priority support",
    ],
    pkg_monthly: "studio_monthly",
    pkg_annual: "studio_annual",
    accent: false,
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [annual, setAnnual] = useState(false);
  const [busy, setBusy] = useState("");
  const [publicBilling, setPublicBilling] = useState(null); // null = loading

  // Pull billing flag from public config for anonymous visitors.
  useEffect(() => {
    if (user) return; // logged in: rely on user.billing_enabled
    let cancelled = false;
    api.get("/config")
      .then(({ data }) => { if (!cancelled) setPublicBilling(!!data.billing_enabled); })
      .catch(() => { if (!cancelled) setPublicBilling(false); });
    return () => { cancelled = true; };
  }, [user]);

  const billingEnabled = user
    ? user.billing_enabled !== false
    : publicBilling === true;

  if (!user && publicBilling === null) {
    return (
      <div className="min-h-screen bg-newsprint flex items-center justify-center" data-testid="pricing-loading">
        <Loader2 size={20} className="animate-spin text-gray-400" />
      </div>
    );
  }
  if (!billingEnabled) {
    return <PreviewPricing user={user} />;
  }

  const choose = async (tier) => {
    if (tier.code === "free") {
      nav(user ? "/app" : "/register");
      return;
    }
    if (!user) {
      nav(`/register?intent=${tier.code}`);
      return;
    }
    const pkg = annual ? tier.pkg_annual : tier.pkg_monthly;
    setBusy(tier.code);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: pkg,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Checkout failed");
      setBusy("");
    }
  };

  const params = new URLSearchParams(window.location.search);
  const cancelled = params.get("cancelled");

  return (
    <div className="min-h-screen bg-newsprint" data-testid="pricing-page">
      <div className="border-b border-gray-300 bg-white/70 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-9 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.25em] text-gray-600">
          <Link to="/" className="hover:text-[#0a0a0a]">← Fount</Link>
          <span>PRICING · {new Date().getFullYear()}</span>
          <Link to={user ? "/app" : "/login"} className="hover:text-[#0a0a0a]">
            {user ? "Open app" : "Sign in"}
          </Link>
        </div>
      </div>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-16 pb-10">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-gray-700 mb-4">
          Pricing · simple ladder
        </div>
        <h1 className="font-display text-6xl lg:text-7xl leading-[0.95] tracking-[-0.03em] font-medium">
          Pick the tier<br />that fits <span className="text-gradient">how you publish</span>.
        </h1>
        <p className="text-lg text-gray-800 leading-relaxed mt-6 max-w-2xl">
          Every plan includes the live 72-hour feed and Claude Sonnet 4.5 generations.
          You only pay for how much you create — and the email, calendar, and multi-show features that come with it.
        </p>

        {cancelled && (
          <div data-testid="pricing-cancelled-banner" className="mt-6 border border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-900 max-w-2xl">
            Checkout cancelled. Your plan is unchanged.
          </div>
        )}

        <div className="mt-10 inline-flex items-center border border-gray-300 bg-white p-1" data-testid="billing-cycle-toggle">
          <button
            data-testid="billing-monthly"
            onClick={() => setAnnual(false)}
            className={`px-5 py-2 text-xs font-mono uppercase tracking-widest transition-colors ${
              !annual ? "bg-[#0a0a0a] text-white" : "text-gray-600 hover:text-[#0a0a0a]"
            }`}
          >
            Monthly
          </button>
          <button
            data-testid="billing-annual"
            onClick={() => setAnnual(true)}
            className={`px-5 py-2 text-xs font-mono uppercase tracking-widest transition-colors inline-flex items-center gap-2 ${
              annual ? "bg-[#0a0a0a] text-white" : "text-gray-600 hover:text-[#0a0a0a]"
            }`}
          >
            Annual
            <span className={`text-[10px] px-1.5 py-0.5 ${annual ? "bg-[#002FA7] text-white" : "bg-[#002FA7]/10 text-[#002FA7]"}`}>
              Save 20%
            </span>
          </button>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border border-gray-300 bg-white rounded-3xl overflow-hidden">
          {TIERS.map((t, i) => {
            const price = annual ? t.annual : t.monthly;
            const priceLabel = price === 0 ? "0" : price.toFixed(2);
            const sub = price === 0 ? "forever" : annual ? "per year" : "per month";
            const monthlyEquiv = annual && t.monthly > 0 ? `$${(t.annual / 12).toFixed(2)}/mo` : null;
            const isCurrent = user?.plan === t.code;

            return (
              <article
                key={t.code}
                data-testid={`pricing-card-${t.code}`}
                className={`p-7 flex flex-col fade-up ${i < TIERS.length - 1 ? "md:border-r border-gray-300" : ""} ${t.accent ? "bg-[#0a0a0a] text-white" : ""}`}
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="flex items-center justify-between">
                  <div className={`font-mono text-[11px] uppercase tracking-[0.25em] ${t.accent ? "text-[#f8d4ff]" : "text-gray-700"}`}>
                    {t.name}
                  </div>
                  {t.accent && (
                    <span className="font-mono text-[10px] uppercase tracking-widest bg-brand-gradient text-white px-2 py-0.5 rounded">
                      Most popular
                    </span>
                  )}
                </div>
                <div className={`text-xs font-mono uppercase tracking-widest mt-1 ${t.accent ? "text-gray-400" : "text-gray-500"}`}>
                  {t.tagline}
                </div>

                <div className="mt-6">
                  <div className="flex items-baseline gap-1">
                    <span className={`font-display text-6xl leading-none tracking-[-0.03em] font-medium ${t.accent ? "text-gradient" : ""}`}>
                      ${priceLabel}
                    </span>
                    <span className={`text-xs font-mono uppercase tracking-widest ml-1 ${t.accent ? "text-gray-400" : "text-gray-500"}`}>
                      {sub}
                    </span>
                  </div>
                  {monthlyEquiv && (
                    <div className={`text-xs font-mono mt-1 ${t.accent ? "text-gray-400" : "text-gray-500"}`}>
                      {monthlyEquiv} effective
                    </div>
                  )}
                </div>

                <ul className="mt-7 space-y-3 flex-1">
                  {t.lines.map((line) => (
                    <li key={line} className="flex items-start gap-2 text-sm leading-snug">
                      <Check size={14} strokeWidth={2} className={t.accent ? "text-[#f8d4ff] mt-0.5" : "text-[#7C3AED] mt-0.5"} />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={`pricing-cta-${t.code}`}
                  disabled={busy === t.code || isCurrent}
                  onClick={() => choose(t)}
                  className={`mt-8 inline-flex items-center justify-center gap-2 px-5 py-3 font-medium text-sm transition-colors rounded-lg ${
                    isCurrent
                      ? "border border-gray-300 text-gray-500 bg-white cursor-default"
                      : t.accent
                      ? "bg-white text-black hover:opacity-90 disabled:opacity-60"
                      : "bg-black text-white hover:bg-gray-800 disabled:opacity-60"
                  }`}
                >
                  {busy === t.code ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : isCurrent ? (
                    <>Current plan</>
                  ) : (
                    <>
                      {t.code === "free" ? "Start free" : "Choose " + t.name}
                      <ArrowRight size={14} strokeWidth={1.5} />
                    </>
                  )}
                </button>
              </article>
            );
          })}
        </div>

        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6 text-xs font-mono uppercase tracking-widest text-gray-600">
          <div className="flex items-center gap-2">
            <Sparkles size={12} strokeWidth={1.5} className="text-[#7C3AED]" />
            Powered by Claude Sonnet 4.5 · Secure checkout via Stripe
          </div>
          <div className="flex items-center gap-2">
            <span className="w-1 h-1 rounded-full bg-gray-400" />
            <Link to="/refund-policy" className="hover:text-black" data-testid="pricing-refund-link">
              Cancel anytime · 7-day guarantee
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-gray-300 bg-white/70 px-6 lg:px-10 py-6 text-[11px] font-mono uppercase tracking-widest text-gray-600 flex items-center justify-between gap-4 flex-wrap">
        <span>Fount · Signal for storytellers</span>
        <div className="flex items-center gap-6">
          <Link to="/support" className="hover:text-black">Support</Link>
          <Link to="/refund-policy" className="hover:text-black">Refunds</Link>
          <Link to="/terms" className="hover:text-black">Terms</Link>
          <Link to="/privacy" className="hover:text-black">Privacy</Link>
          <span className="text-gray-400">Cancel anytime · No card on Free</span>
        </div>
      </footer>
    </div>
  );
}

function PreviewPricing({ user }) {
  return (
    <div className="min-h-screen bg-newsprint" data-testid="pricing-preview">
      <div className="border-b border-gray-300 bg-white/70 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-9 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.25em] text-gray-600">
          <Link to="/" className="hover:text-[#0a0a0a]">← Fount</Link>
          <span>EARLY ACCESS</span>
          <Link to={user ? "/app" : "/login"} className="hover:text-[#0a0a0a]">
            {user ? "Open app" : "Sign in"}
          </Link>
        </div>
      </div>

      <section className="max-w-3xl mx-auto px-6 lg:px-10 pt-20 pb-24">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-gray-700 mb-4">
          Pricing · early access
        </div>
        <h1 className="font-display text-6xl lg:text-7xl leading-[0.95] tracking-[-0.03em] font-medium">
          Fount is free<br /><span className="text-gradient">during preview</span>.
        </h1>
        <p className="text-lg text-gray-800 leading-relaxed mt-6 max-w-xl">
          We&apos;re shipping new features every week. Plans launch later this year — and everyone who joins now gets early-access pricing locked in.
        </p>

        <div className="mt-12 relative overflow-hidden rounded-3xl border border-transparent shadow-[0_24px_64px_-32px_rgba(124,58,237,0.35)]">
          <div className="absolute inset-0 bg-brand-gradient" aria-hidden="true" />
          <div className="relative p-8 lg:p-10 text-white">
            <div className="flex items-center justify-between">
              <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/85">
                Early access
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest bg-white/20 text-white px-2 py-1 rounded">
                Active
              </span>
            </div>
            <div className="font-display text-7xl tracking-[-0.03em] font-medium mt-4 leading-none">
              Free
            </div>
            <div className="text-sm text-white/85 mt-2">
              No card required · No usage caps during preview
            </div>

            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              {[
                "Live 72-hour story feed across 12 niches",
                "Unlimited story-to-packet generations",
                "Daily Brief email delivery (06:00 UTC)",
                "Weekly Calendar — one-click Mon–Fri plan",
                "Copy as ready-to-record script",
                "Save unlimited story packets",
              ].map((line) => (
                <li key={line} className="flex items-start gap-2">
                  <Check size={16} strokeWidth={2.2} className="text-white mt-0.5" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to={user ? "/app" : "/register"}
                data-testid="preview-cta"
                className="inline-flex items-center gap-2 bg-white text-black hover:bg-gray-100 rounded-lg px-6 py-3.5 font-medium text-sm transition-colors"
              >
                {user ? "Open Fount" : "Get early access"}
                <ArrowRight size={14} strokeWidth={1.8} />
              </Link>
              <span className="text-xs font-mono uppercase tracking-widest text-white/80">
                Plans launch later this year
              </span>
            </div>
          </div>
        </div>

        <div className="mt-10 flex items-center gap-3 text-xs font-mono uppercase tracking-widest text-gray-600">
          <Sparkles size={12} strokeWidth={1.5} className="text-[#7C3AED]" />
          Powered by Claude Sonnet 4.5
        </div>
      </section>

      <footer className="border-t border-gray-300 bg-white/70 px-6 lg:px-10 py-6 text-[11px] font-mono uppercase tracking-widest text-gray-600 flex items-center justify-between gap-4 flex-wrap">
        <span>Fount · Signal for storytellers</span>
        <div className="flex items-center gap-6">
          <Link to="/support" className="hover:text-black">Support</Link>
          <Link to="/refund-policy" className="hover:text-black">Refunds</Link>
          <Link to="/terms" className="hover:text-black">Terms</Link>
          <Link to="/privacy" className="hover:text-black">Privacy</Link>
          <span className="text-gray-400">Early access · No card required</span>
        </div>
      </footer>
    </div>
  );
}
